import {
	Delete,
	Download,
	FolderInput,
	Pencil,
	Share2,
	Trash2,
} from "lucide-react";
import { useIsDocumentTransferPending } from "web_myplann/Api/Document/useIsDocumentTransferPending";
import { useRemoveSavedDocumentMutation } from "web_myplann/Api/Document/useRemoveSavedDocumentMutation";
import { useSaveDocumentMutation } from "web_myplann/Api/Document/useSaveDocumentMutation";
import { useSavedDocumentIdsQuery } from "web_myplann/Api/Document/useSavedDocumentIdsQuery";
import { useShareDocumentMutation } from "web_myplann/Api/Document/useShareDocumentMutation";
import {
	type DocumentDTO,
	DocumentLocalTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { DrawerAction } from "web_myplann/Shared/Components/Redesign/ActionsDrawerRow";

const R = Resources.Portfolio.List.DocumentActionsDrawer.Actions;

export type DocumentActionDrawer = "rename" | "move" | "delete";

type Args = {
	item: DocumentDTO;
	onClose: () => void;
	onOpenDrawer: (drawer: DocumentActionDrawer) => void;
};

export const useDocumentActions = ({
	item,
	onClose,
	onOpenDrawer,
}: Args): DrawerAction[] => {
	const { t } = useResource();
	const { data: savedDocumentIds } = useSavedDocumentIdsQuery();
	const { mutateAsync: saveDocument, isPending: isSaving } =
		useSaveDocumentMutation();
	const { mutateAsync: removeSavedDocument, isPending: isRemoving } =
		useRemoveSavedDocumentMutation();
	const { mutateAsync: shareDocument, isPending: isSharing } =
		useShareDocumentMutation();
	const isProcessing = useIsDocumentTransferPending();

	const isLocal = item.type === DocumentLocalTypeEnum.Local;
	const isDownloaded = savedDocumentIds?.includes(item.id) ?? false;

	const handleDownload = async () => {
		try {
			await saveDocument({
				id: item.id,
				fallbackTitle: item.title,
			});
		} catch {
			return;
		}
		onClose();
	};

	const handleRemoveDownload = async () => {
		try {
			await removeSavedDocument({ id: item.id, filename: item.filename });
		} catch {
			return;
		}
		onClose();
	};

	const handleShare = async () => {
		try {
			await shareDocument({ id: item.id, fallbackTitle: item.title });
		} catch {
			return;
		}
		onClose();
	};

	return [
		{
			key: "download",
			label: t(R.save),
			icon: Download,
			onClick: handleDownload,
			isPending: isSaving,
			disabled: isProcessing,
			hidden: isDownloaded,
		},
		{
			key: "removeDownload",
			label: t(R.removeDownload),
			icon: Delete,
			onClick: handleRemoveDownload,
			isPending: isRemoving,
			disabled: isProcessing,
			hidden: !isDownloaded,
		},
		{
			key: "share",
			label: t(R.share),
			icon: Share2,
			onClick: handleShare,
			isPending: isSharing,
			disabled: isProcessing,
		},
		{
			key: "rename",
			label: t(R.rename),
			icon: Pencil,
			onClick: () => onOpenDrawer("rename"),
			disabled: isProcessing,
			hidden: !isLocal,
		},
		{
			key: "move",
			label: t(R.move),
			icon: FolderInput,
			onClick: () => onOpenDrawer("move"),
			disabled: isProcessing,
		},
		{
			key: "delete",
			label: t(R.delete),
			icon: Trash2,
			onClick: () => onOpenDrawer("delete"),
			isDestructive: true,
			disabled: isProcessing,
			hidden: !isLocal,
		},
	];
};
